// Default input factories — used by the store. State layer.

import type {
  AllocationAssumptions,
  CapTable,
  ComplianceInputs,
  DiscountInputs,
  EquityValueAnchor,
  ShareInstrument,
  ValuationInputs,
} from '@backsolve/shared-types';
import { SOLVER_DEFAULT_MAX_ITERATIONS, SOLVER_DEFAULT_TOLERANCE } from '@backsolve/shared-logic/constants';
import { ulid } from '@/id';

export function defaultCommonInstrument(name = 'Common Stock'): ShareInstrument {
  return {
    id: `inst_${ulid()}`,
    name,
    kind: 'common',
    sharesOutstanding: 10_000_000,
    sharesAuthorized: 10_000_000,
    issuePrice: 0.0001,
    issueDate: '2021-01-01',
    liquidationMultiple: null,
    participation: null,
    participationCap: null,
    seniority: null,
    conversionRatio: null,
    strikePrice: null,
    vestingMonths: null,
    expirationDate: null,
    antiDilution: null,
    notes: '',
  };
}

export function emptyCapTable(entityName = 'New Entity'): CapTable {
  return {
    id: `cap_${ulid()}`,
    version: 1,
    asOfDate: new Date().toISOString().slice(0, 10),
    entityName,
    instruments: [defaultCommonInstrument()],
    convertibles: [],
    currency: 'USD',
  };
}

export function defaultAssumptions(): AllocationAssumptions {
  return {
    volatility: 0.5,
    timeToLiquidity: 5.0,
    riskFreeRate: 0.0425,
    dividendYield: 0,
    method: 'opm',
    methodRationale: '',
  };
}

export function defaultEquityValue(): EquityValueAnchor {
  return {
    source: 'backsolve',
    anchorInstrumentId: null,
    anchorPricePerShare: null,
    directEquityValue: null,
    armLengthFlag: true,
    transactionDate: null,
  };
}

export function defaultDiscounts(): DiscountInputs {
  return {
    dlomModel: 'fixed',
    dlomPercentage: 0.25,
    minorityDiscount: 0,
  };
}

export function defaultCompliance(): ComplianceInputs {
  return {
    valuationDate: new Date().toISOString().slice(0, 10),
    grantDate: null,
    safeHarbor: 'none',
  };
}

export function defaultValuationInputs(_entityName: string, cap: CapTable): ValuationInputs {
  return {
    valuationId: `val_${ulid()}`,
    capTableSnapshot: cap,
    assumptions: defaultAssumptions(),
    equityValue: defaultEquityValue(),
    discounts: defaultDiscounts(),
    compliance: defaultCompliance(),
    solverTolerance: SOLVER_DEFAULT_TOLERANCE,
    solverMaxIterations: SOLVER_DEFAULT_MAX_ITERATIONS,
  };
}
